/**
 * Converts Imgur page URLs to direct media URLs (images and videos)
 * @param {string} url - The URL to convert
 * @returns {string} - Direct media URL or original URL if not an Imgur link
 */
export const convertImgurUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  
  const trimmedUrl = url.trim();
  
  // Pattern: https://imgur.com/abc123 -> https://i.imgur.com/abc123
  const imgurPagePattern = /^https?:\/\/(www\.)?imgur\.com\/([a-zA-Z0-9]+)$/;
  const match = trimmedUrl.match(imgurPagePattern);
  
  if (match) {
    const mediaId = match[2];
    // Default to .jpg for images (most common format on Imgur)
    // Users can manually add .mp4, .gifv, .png if needed
    return `https://i.imgur.com/${mediaId}.jpg`;
  }
  
  // Pattern 2: https://imgur.com/a/abc123 (album) - can't auto-convert
  // Pattern 3: https://imgur.com/gallery/abc123 (gallery) - can't auto-convert
  // Just return original URL for these cases
  
  // If already a direct link (https://i.imgur.com/...) or not imgur, return as-is
  return trimmedUrl;
};

/**
 * Detects if a URL is an Imgur page link that can be converted
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a convertible Imgur page link
 */
export const isConvertibleImgurUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const imgurPagePattern = /^https?:\/\/(www\.)?imgur\.com\/([a-zA-Z0-9]+)$/;
  return imgurPagePattern.test(url.trim());
};

/**
 * Gets all possible Imgur media URLs (for trying multiple formats)
 * @param {string} url - The URL to convert
 * @returns {Array} - Array of possible direct URLs to try
 */
export const getImgurMediaUrls = (url) => {
  if (!url || typeof url !== 'string') return [url];
  
  const trimmedUrl = url.trim();
  const imgurPagePattern = /^https?:\/\/(www\.)?imgur\.com\/([a-zA-Z0-9]+)$/;
  const match = trimmedUrl.match(imgurPagePattern);
  
  if (match) {
    const mediaId = match[2];
    // Return multiple possible URLs to try (images first, then videos)
    return [
      `https://i.imgur.com/${mediaId}.jpg`,
      `https://i.imgur.com/${mediaId}.png`,
      `https://i.imgur.com/${mediaId}.gif`,
      `https://i.imgur.com/${mediaId}.mp4`,
      `https://i.imgur.com/${mediaId}.gifv`
    ];
  }
  
  return [trimmedUrl];
};
